import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { AccountConfigurationModel } from 'src/app/models/account-configuration.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { AppService } from 'src/app/app.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from '../../shared/rbac.service';
import { AccountSettingsService } from './account-settings.service';
import { AccountRiskMatrixComponent } from '../account-risk-matrix/account-risk-matrix.component';
import { AccountHierarchyOfControlsComponent } from '../account-hierarchy-of-controls/account-hierarchy-of-controls.component';
import { CURRENCIES, Currency } from '../../constants/currencies.constants';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountSettingsComponent implements OnInit {
  config: AccountConfigurationModel = {} as AccountConfigurationModel;

  inProgress: boolean = false;

  pandemicInProgress: boolean = false;

  canEdit: boolean = false;

  selectedTabIndex: number = 0;

  auto_site_signout_duration_options: number[] = [2, 3, 4, 5, 6];

  currencies: Currency[] = CURRENCIES;

  constructor(
    private configService: AccountSettingsService,
    private api: ApiRequestService,
    public app: AppService,
    public utils: UtilsService,
    private rbac: RBACService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  // Section visibility flags, resolved once in ngOnInit() so the template does not re-evaluate
  // app.planCan() or rbac.userCanAccess() on every change detection cycle.
  //
  // Each section configures a DIFFERENT feature, so each needs both gates: the account's plan must
  // include that feature, and the user must have a permission on it. Holding account-settings:edit
  // means "may configure the account", not "may configure every feature in it", so a user with only
  // Settings and RBAC permissions was previously shown the Assets, Hazards, Sites and Safety Plan
  // settings for features they cannot otherwise see.
  //
  // The permission side requires the feature's EDIT action specifically. Every control in these
  // sections writes, and none of them has a read-only rendering, so accepting any action would let a
  // view-only user change how a feature behaves account-wide (someone with registers-assets:view
  // changing the currency every asset cost is reported in). View, create and delete describe what a
  // user may do to a feature's RECORDS and say nothing about authority over its configuration, so
  // edit is the only one of the four that matches. This is deliberately stricter than the left
  // navigation, which shows a feature on any action because it is answering a different question.
  showAssetsSection: boolean = false;
  showHazardsSection: boolean = false;
  showSitesSection: boolean = false;
  showSafetyPlanSection: boolean = false;
  showSiteDeclarationsTab: boolean = false;

  // The Notifications section mixes features, so its rows are gated individually. The section itself
  // is always rendered because the main-contact row belongs to no particular feature.
  showInductionNotifications: boolean = false;
  showSiteNotifications: boolean = false;

  ngOnInit() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_SETTINGS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to change account settings.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.canEdit = true;

    // Resolve the section visibility flags used by the template.
    this.showAssetsSection = this.canConfigure(
      AppService.FEATURES.ASSETS,
      RBACService.RBAC_F.F_REGISTERS_ASSETS
    );
    this.showHazardsSection = this.canConfigure(
      AppService.FEATURES.HAZARDS,
      RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS
    );
    this.showSitesSection = this.canConfigure(
      AppService.FEATURES.SITES,
      RBACService.RBAC_F.F_SITES
    );
    this.showSafetyPlanSection = this.canConfigure(
      AppService.FEATURES.SAFETY_PLANS,
      RBACService.RBAC_F.F_SAFETY_PLANS
    );
    this.showInductionNotifications = this.canConfigure(
      AppService.FEATURES.INDUCTIONS,
      RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS
    );

    // Site sign-in and arrival notifications are site behaviour, so they follow the Sites gate.
    this.showSiteNotifications = this.showSitesSection;

    // The Site Declarations tab configures the site sign-in declaration, so it follows Sites too.
    this.showSiteDeclarationsTab = this.showSitesSection;

    // Load configuration data
    this.configService
      .find(this.app.account.id)
      .subscribe((response) => (this.config = response.data));

    // Load account data for pandemic tab
    this.api.makeRequest('get', 'v1/account').then((response: any) => {
      this.app.account.apply(response.data);
    });

    // Check for tab query parameter. The index is resolved against the tabs actually rendered,
    // because a hardcoded index breaks as soon as a conditional tab is hidden.
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab) {
      // Unknown or unavailable tab names fall back to the first tab.
      this.selectedTabIndex = Math.max(0, this.visibleTabNames().indexOf(tab));
    }
  }

  /**
   * Whether a settings section that configures another feature should be shown.
   *
   * Requires both gates: the account's subscription must include the feature, and the user must hold
   * that feature's EDIT permission. account-settings:edit alone is not enough, because it says nothing
   * about whether this user works with the feature being configured, and the feature's view, create or
   * delete permissions are not enough either, because every control in these sections is a write.
   *
   * Mirrors Controller::canConfigureFeature() on the API, which is the authoritative gate.
   *
   * @param planFeature The AppService.FEATURES key the section belongs to.
   * @param rbacFeature The RBACService.RBAC_F slug the section belongs to.
   */
  private canConfigure(planFeature: string, rbacFeature: string): boolean {
    if (!this.app.planCan(planFeature)) {
      return false;
    }

    return this.rbac.userCanAccess(
      [RBACService.getPermission(rbacFeature, RBACService.RBAC_A.A_EDIT)],
      true
    );
  }

  /**
   * The tab names in template order, with conditionally rendered tabs excluded so the positions match
   * the DOM. Keep this in sync with account-settings.component.html whenever a tab is added, removed,
   * reordered, or made conditional.
   */
  private visibleTabNames(): string[] {
    return [
      'settings',
      // "Site Declarations" is only rendered when the user can configure the Sites feature.
      ...(this.showSiteDeclarationsTab ? ['site-declarations'] : []),
      'data-sync'
    ];
  }

  onSaveConfiguration(form: NgForm) {
    if (!form.valid || this.inProgress) {
      return;
    }

    this.inProgress = true;

    this.configService.update(this.config.id, this.config).subscribe(
      (response: any) => {
        // Also persist account-level fields (e.g. site arrival notifications)
        this.api
          .makeRequest(
            'put',
            `v1/account/${this.app.account.id}`,
            this.app.account
          )
          .then((accountResponse: any) => {
            this.app.account.apply(accountResponse.data);
            this.utils.showToast('Updated account settings');
            this.inProgress = false;
          })
          .catch(() => {
            this.inProgress = false;
          });
      },
      () => {
        this.inProgress = false;
      }
    );
  }

  onSavePandemic(form: NgForm) {
    if (!form.valid || this.pandemicInProgress) {
      return;
    }

    this.pandemicInProgress = true;

    this.api
      .makeRequest('put', `v1/account/${this.app.account.id}`, this.app.account)
      .then((response: any) => {
        this.app.account.apply(response.data);
        this.utils.showToast('Updated site declarations');
        this.pandemicInProgress = false;
      })
      .catch(() => {
        this.pandemicInProgress = false;
      });
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;

  // Both dialogs upload a Hazards & Risks definition image, so they carry the same gate as the
  // section that hosts their buttons. The buttons are hidden without it, but these methods can still
  // be reached programmatically, so the guard is repeated here.
  onUploadRiskMatrix() {
    if (!this.showHazardsSection) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to configure hazards and risks.'
      );
      return;
    }

    this.utils.showComponentDialog(AccountRiskMatrixComponent);
  }

  onUploadHierarchyOfControl() {
    if (!this.showHazardsSection) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to configure hazards and risks.'
      );
      return;
    }

    this.utils.showComponentDialog(AccountHierarchyOfControlsComponent);
  }
}
